import { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface CancelTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onValidate: (cancellationDate: string, cancellationReason: string) => void;
  transactionId: string;
}

export default function CancelTransactionModal({ isOpen, onClose, onValidate, transactionId }: CancelTransactionModalProps) {
  const { user } = useAuth();
  const [cancellationDate, setCancellationDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Add escape key handler
  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!cancellationDate) {
        setError('La date d\'annulation est requise');
        setLoading(false);
        return;
      }

      if (!cancellationReason.trim()) {
        setError('La cause d\'annulation est requise');
        setLoading(false);
        return;
      }

      // Récupérer le statut précédent de la transaction
      const { data: transactionData } = await supabase
        .from('transactions')
        .select('status')
        .eq('id', transactionId)
        .single();

      const previousStatus = transactionData?.status || 'unknown';

      // Envoyer une notification email pour l'annulation
      try {
        console.log('Sending cancellation notification email');
        await sendStatusChangeNotification(transactionId, previousStatus, 'annule');
        console.log('Cancellation notification sent successfully');
      } catch (emailError) {
        console.error('Error sending cancellation notification:', emailError);
        // Ne pas faire échouer l'annulation si l'email échoue
      }

      onValidate(cancellationDate, cancellationReason);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-bold mb-6">
          Annulation de la transaction
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date d'annulation
            </label>
            <input
              type="date"
              value={cancellationDate}
              onChange={(e) => setCancellationDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cause d'annulation
            </label>
            <textarea
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              rows={4}
              placeholder="Veuillez indiquer la raison de l'annulation..."
              required
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Annulation...' : 'Confirmer l\'annulation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}